import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { GlobalService } from '@base/services/private/global.service';
import { ModalService } from '@base/services/private/modal.service';
import { ThemeService } from '@base/services/private/theme.service';
import { Subscription } from 'rxjs';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

// local enum for active button states
enum ActiveButton {
  CREATE_VIDEO_MEETING = 'create-video-meeting',
}

@Component({
  selector: 'app-modal-create-video-meeting',
  imports: [ModalHeaderComponent, ModalBodyComponent, ModalFooterComponent, TranslatePipe, ReactiveFormsModule],
  templateUrl: './modal-create-video-meeting.component.html',
  styleUrl: './modal-create-video-meeting.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalCreateVideoMeetingComponent implements OnInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly themeSrv = inject(ThemeService);
  private readonly globalSrv = inject(GlobalService);
  private readonly formBuilder = inject(FormBuilder);

  @Input() modalId = '';
  @Input() utcInfo = Intl.DateTimeFormat().resolvedOptions().timeZone;
  @Input() matchAppEndDate = '';

  activeButton = '';
  dateToday = new Date().toISOString().substring(0, 10);

  isHttpRequestRunning = false;
  isBgDarkMode = this.themeSrv.isBgDarkMode;

  form = this.formBuilder.group({
    step: ['immediate', [Validators.required]],
    date: [''],
    time: [''],
  });

  ActiveButton = ActiveButton;
  httpRequestObserver$: Subscription | null = null;

  ngOnInit(): void {
    this.listenHttpRequestState();
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * handle change type of meeting
   * @param type - 'immediate' | 'later'
   */
  onChangeType(type: 'immediate' | 'later'): void {
    this.form.patchValue({ step: type });

    const dateControl = this.form.get('date');
    const timeControl = this.form.get('time');

    this.form.patchValue({ date: '', time: '' });

    if (type === 'immediate') {
      dateControl?.setValidators([]);
      timeControl?.setValidators([]);
    }

    if (type === 'later') {
      dateControl?.setValidators([Validators.required]);
      timeControl?.setValidators([Validators.required]);
    }

    dateControl?.updateValueAndValidity();
    timeControl?.updateValueAndValidity();
  }

  /**
   * close modal
   */
  close(): void {
    this.modalSrv.close();
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
      },
    });
  }

  /**
   * cancel video meeting
   */
  createVideoMeeting(): void {
    this.activeButton = ActiveButton.CREATE_VIDEO_MEETING;
    this.form.markAllAsDirty();
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      console.error('Form is invalid');
      this.activeButton = '';
      return;
    }

    this.globalSrv.setHttpRequestState(true);
    setTimeout(() => {
      this.activeButton = '';
      this.globalSrv.setHttpRequestState(false);
      this.modalSrv.close(this.modalId, {
        key: 'CREATE_VIDEO_MEETING',
        value: this.form.value,
      });
    }, 300);
  }
}
